<template>
    <v-row no-gutters :class="{'mb-4': !minify}">
        <v-col v-if="!minify" cols="12" md="2" class="d-flex align-center flex-column justify-center justify-md-start">
            <template v-if="airlines && airlines[0]">
                <v-img
                    v-for="airline in airlines"
                    :key="airline"
                    :src="logo(airline)"
                    :max-height="50"
                    class="full-width"
                />
            </template>
            <span v-else class="text-caption py-2">
                {{ $t('no_airline_logo') }}
            </span>
        </v-col>
        <v-col cols="12" :md="minify ? 12 : 10">
            <v-row :no-gutters="minify" dense>
                <flight-itinerary-point
                    :point="departure"
                    :size="minify ? 4 : 4"
                    :departure-label="departureLabel"
                    direction="departure"
                />
                <v-col :cols="minify ? 4 : 4" class="d-flex flex-column" :class="{'px-1': minify}">
                    <v-row no-gutters>
                        <v-col v-if="itinerary.duration" cols="12" class="mb-1 text-center" align-self="end">
                            {{ $filters.durationFormat(itinerary.duration) }}
                        </v-col>
                        <v-col v-else cols="12" class="mb-1 text-center" align-self="end">
                            {{ $t('no_duration') }}
                        </v-col>
                        <v-col cols="12" class="mb-1 d-flex align-center">
                            <v-icon class="text-body-2 float-left">mdi-airplane-takeoff</v-icon>
                            <div class="line">
                                <div v-if="hasStops" class="stops-container">
                                    <span
                                        v-for="(stop, index) in stops"
                                        :key="`stop-${index}`"
                                        :class="{'mr-2': stops.length !== index + 1}"
                                    />
                                </div>
                            </div>
                            <v-icon class="text-body-2" end>mdi-airplane-landing</v-icon>
                        </v-col>
                        <v-col v-if="hasStops" cols="12" class="mb-1 d-flex flex-column align-center">
                            <div class="text-red">
                                {{ $t('filters_stops', stops.length) }}
                            </div>
                            <div>
                                <v-tooltip v-for="stop in stops" :key="`stops-${stop.airportCode}`" location="bottom">
                                    <template v-slot:activator="{props}">
                                        <div class="text-caption" v-bind="props">
                                            {{ stop.airportCode }}
                                        </div>
                                    </template>
                                    <span>
                                        <v-icon start class="text-white">mdi-airplane</v-icon>
                                        {{ getAirportName(stop) }}
                                    </span>
                                </v-tooltip>
                            </div>
                        </v-col>
                    </v-row>
                </v-col>
                <flight-itinerary-point
                    :point="arrival"
                    :size="minify ? 4 : 4"
                    :arrival-label="arrivalLabel"
                    direction="arrival"
                />
            </v-row>
        </v-col>
    </v-row>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import FlightItineraryPoint from '@/components/flights/snippets/FlightItineraryPoint.vue'
    import {getOfferAirportName} from '@/utils/helpers'
    import {fullUrl} from '~src/utils/host-adapter'

    @Component({
        components: {
            FlightItineraryPoint,
        },
    })
    export class FlightItinerary extends Vue {
        @Prop({required: true}) itinerary
        @Prop({default: false}) minify

        logo(airline) {
            return fullUrl(`/stat/images/airlines/${airline}.gif`)
        }

        getAirportName(data) {
            if (data.airportCode) {
                return getOfferAirportName(data)
            }
            return ''
        }

        get departure() {
            return this.itinerary.segments[0].departure
        }

        get arrival() {
            return this.itinerary.segments[this.itinerary.segments.length - 1].arrival
        }

        get departureLabel() {
            return `${this.departure.airportDescription.cityName}, ${this.departure.airportDescription.countryName}`
        }

        get arrivalLabel() {
            return `${this.arrival.airportDescription.cityName}, ${this.arrival.airportDescription.countryName}`
        }

        get airlines() {
            const airlines = this.itinerary.segments.map(({marketingAirline}) => marketingAirline)
            return Array.from(new Set(airlines))
        }

        get hasStops() {
            return this.itinerary.segments.length > 1
        }
        get stops() {
            const stops = this.itinerary.segments.map(({arrival}) => arrival)
            stops.pop()
            return stops
        }
    }

    export default FlightItinerary
</script>

<style lang="scss" scoped>
    .line {
        width: 90%;
        border-top: 2px solid black;
        position: relative;

        & .stops-container {
            position: absolute;
            left: 50%;
            margin-top: -7px;
            transform: translateX(-50%);
            display: flex;
            justify-content: space-around;

            & span {
                border-radius: 50%;
                width: 12px;
                height: 12px;
                display: flex;
                background: red;
                border: 2px solid white;
            }
        }
    }
</style>
